import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, withModifiers as _withModifiers, withKeys as _withKeys, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = ["aria-label"]
const _hoisted_2 = ["data-index", "inputmode", "value", "disabled", "aria-label", "onInput", "onKeydown", "onFocus", "onBlur"]

import {
  computed, onMounted, ref, watch,
} from 'vue';

/**
 * SegmentedCodeInput
 *
 * A reusable, logic-only segmented code/OTP/PIN input.
 * Renders N single-character fields with full keyboard + mobile support:
 * paste-to-fill, auto-advance, backspace/delete, arrow navigation,
 * focus/blur tracking, and uppercase alphanumeric normalization.
 *
 * No styling is included; consumers provide their own via the `field` slot
 * or by targeting the exposed classes / data attributes.
 */

interface Props {
  /** Number of code segments (digits/characters). */
  length?: number;
  /** v-model value: the full assembled code string. */
  modelValue?: string;
  /** Disable all inputs (e.g. while submitting). */
  disabled?: boolean;
  /** Autofocus the first field on mount. */
  autofocus?: boolean;
  /** Accessible label template; `{number}` is replaced with the 1-based index. */
  ariaLabelTemplate?: string;
  /** Accessible group label for the whole control. */
  ariaGroupLabel?: string;
  /** Submit automatically once all segments are filled. */
  submitOnComplete?: boolean;
  /** Only allow numeric digits (0-9). */
  numericOnly?: boolean;
  /** Custom list of allowed characters (e.g. "0123456789ABCDEF" or "0-9A-F"). */
  allowedCharacters?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SegmentedCodeInput',
  props: {
    length: { default: 4 },
    modelValue: { default: '' },
    disabled: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: true },
    ariaLabelTemplate: { default: 'Character {number}' },
    ariaGroupLabel: { default: 'Code input' },
    submitOnComplete: { type: Boolean, default: false },
    numericOnly: { type: Boolean, default: false },
    allowedCharacters: { default: undefined }
  },
  emits: ["update:modelValue", "complete", "submit", "invalid"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

// Allowed-character mask: uppercase alphanumeric only by default.
const stripDisallowed = computed(() => {
  if (props.numericOnly) {
    return /[^0-9]/g;
  }

  if (props.allowedCharacters) {
    return new RegExp(`[^${props.allowedCharacters}]`, 'gi');
  }

  return /[^A-Z0-9]/g;
});

const digits = ref<Array<string>>(Array.from({ length: props.length }, () => ''));
const inputs = ref<Array<HTMLInputElement>>([]);
const activeIndex = ref<number | undefined>(undefined);

const lastIndex = computed(() => props.length - 1);

const normalize = (value: string): string => (value || '').toUpperCase().replace(stripDisallowed.value, '');

const assembled = computed(() => digits.value.join(''));

const isComplete = computed(() => digits.value.every((d) => d !== '') && digits.value.length === props.length);

const clampIndex = (index: number): number => Math.max(0, Math.min(lastIndex.value, index));

const emitValue = () => {
  const { value } = assembled;

  emit('update:modelValue', value);

  if (isComplete.value) {
    emit('complete', value);

    if (props.submitOnComplete) {
      emit('submit', value);
    }
  }
};

// Programmatically focus + select a field, guarding against missing refs.
const focusIndex = (index: number, select = true): void => {
  const el = inputs.value[index];

  if (!el) {
    return;
  }

  el.focus();

  if (select) {
    el.select();
  }
};

const setActiveIndex = (index: number | undefined, focus = true): void => {
  if (index === undefined) {
    activeIndex.value = undefined;

    return;
  }

  const clamped = clampIndex(index);

  activeIndex.value = clamped;

  if (focus) {
    focusIndex(clamped);
  }
};

// Set a single segment, auto-advancing to the next field.
const setDigit = (rawValue: string, index: number): void => {
  const normalized = normalize(rawValue);

  if (!normalized && rawValue !== '') {
    // Invalid character(s) entered: ignore them and stay on current field.
    emit('invalid');

    return;
  }

  // Only keep the last typed character per segment.
  digits.value[index] = normalized.slice(-1);

  emitValue();

  if (normalized && index < lastIndex.value) {
    setActiveIndex(index + 1);
  } else if (normalized) {
    inputs.value[index]?.blur();
  }
};

// Fill the whole control from a string (e.g. paste or external set).
// Returns true if a full valid code was applied.
const setCode = (code: string): boolean => {
  const normalized = normalize(code);

  // Fill as many segments as we can, even on partial codes.
  for (let i = 0; i < props.length; i += 1) {
    digits.value[i] = normalized[i] ?? '';
  }

  emitValue();

  if (normalized.length < props.length) {
    emit('invalid');

    setActiveIndex(Math.min(normalized.length, lastIndex.value));

    return false;
  }

  setActiveIndex(lastIndex.value);

  return true;
};

const clear = (): void => {
  digits.value = Array.from({ length: props.length }, () => '');

  emitValue();

  setActiveIndex(0);
};

// Read the typed character from the input event (cast kept out of the template).
const onInput = (e: Event, index: number): void => {
  const target = e.target as HTMLInputElement;

  setDigit(target.value, index);

  // Force the input field's value to stay in sync with our state.
  // This removes invalid characters and reflects normalization (like uppercase).
  target.value = digits.value[index];
};

const onFocus = (index: number): void => {
  setActiveIndex(index, false);
};

const onBlur = (index: number): void => {
  if (activeIndex.value !== index) {
    return; // Focus already moved elsewhere; ignore stale blur.
  }

  setActiveIndex(undefined, false);
};

const onArrowLeft = (): void => {
  if (activeIndex.value === undefined) {
    return;
  }

  if (activeIndex.value > 0) {
    setActiveIndex(activeIndex.value - 1);
  }
};

const onArrowRight = (): void => {
  if (activeIndex.value === undefined) {
    return;
  }

  if (activeIndex.value < lastIndex.value) {
    setActiveIndex(activeIndex.value + 1);
  }
};

// Normalize Backspace/Delete across browsers using key or keyCode.
const resolveDeleteKey = (e: KeyboardEvent): 'Backspace' | 'Delete' | undefined => {
  if (e.key === 'Backspace' || e.key === 'Delete') {
    return e.key;
  }

  const code = e.which || e.keyCode;

  if (code === 8) {
    return 'Backspace';
  }

  if (code === 46) {
    return 'Delete';
  }

  return undefined;
};

const onDelete = (e: KeyboardEvent): void => {
  if (activeIndex.value === undefined) {
    return;
  }

  const key = resolveDeleteKey(e);
  const index = activeIndex.value;

  if (key === 'Backspace') {
    if (digits.value[index]) {
      // Current field has a value: clear it, stay here.
      digits.value[index] = '';

      setActiveIndex(index);
    } else {
      // Current field is empty: move back and clear the previous one.
      const prev = clampIndex(index - 1);

      digits.value[prev] = '';

      setActiveIndex(prev);
    }

    emitValue();
  } else if (key === 'Delete') {
    digits.value[index] = '';

    setActiveIndex(index);

    emitValue();
  }
};

const onEnter = (): void => {
  const { value } = assembled;

  emit('submit', value);

  if (isComplete.value) {
    emit('complete', value);
  }
};

const onPaste = (e: ClipboardEvent): void => {
  e.preventDefault();
  e.stopPropagation();

  const pasted = e.clipboardData?.getData('text')?.trim().replace(/\s+/g, '') ?? '';

  if (pasted) {
    setCode(pasted);
  }
};

// Keep internal state in sync if the parent changes modelValue externally.
watch(
  () => props.modelValue,
  (value) => {
    if (normalize(value) !== assembled.value) {
      const normalized = normalize(value);

      for (let i = 0; i < props.length; i += 1) {
        digits.value[i] = normalized[i] ?? '';
      }
    }
  },
  { immediate: true },
);

// Re-size the internal arrays if length changes at runtime.
watch(
  () => props.length,
  (len) => {
    const next = Array.from({ length: len }, (_, i) => digits.value[i] ?? '');

    digits.value = next;
  },
);

onMounted(() => {
  if (props.autofocus && !props.disabled) {
    setActiveIndex(0);
  }
});

// Expose imperative methods for parent components.
__expose({
  clear,
  setCode,
  focus: () => setActiveIndex(0),
  isComplete,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "segmented-code-input",
    role: "group",
    "aria-label": __props.ariaGroupLabel
  }, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.length, (i) => {
      return (_openBlock(), _createElementBlock("div", {
        key: i,
        class: "segmented-code-input__field__container"
      }, [
        _createElementVNode("input", {
          ref_for: true,
          ref_key: "inputs",
          ref: inputs,
          class: "segmented-code-input__field",
          "data-index": i - 1,
          type: "text",
          inputmode: __props.numericOnly ? 'numeric' : 'text',
          autocapitalize: "characters",
          autocomplete: "one-time-code",
          spellcheck: "false",
          maxlength: "1",
          value: digits.value[i - 1],
          disabled: __props.disabled,
          "aria-label": __props.ariaLabelTemplate.replace('{number}', String(i)),
          onInput: ($event: any) => (onInput($event, i - 1)),
          onPaste: onPaste,
          onKeydown: [
            _withKeys(_withModifiers(onDelete, ["prevent","stop"]), ["delete"]),
            _withKeys(_withModifiers(onArrowLeft, ["prevent","stop"]), ["left"]),
            _withKeys(_withModifiers(onArrowRight, ["prevent","stop"]), ["right"]),
            _withKeys(_withModifiers(onEnter, ["prevent","stop"]), ["enter"])
          ],
          onFocus: ($event: any) => (onFocus(i - 1)),
          onBlur: ($event: any) => (onBlur(i - 1))
        }, null, 40, _hoisted_2)
      ]))
    }), 128))
  ], 8, _hoisted_1))
}
}

})